'use client';

import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { cartExecuteOttPayment, cartExecutePayment } from 'actions/cart';
import cartApi from 'api/cart.api';
import config from 'config';
import { creditCardSchema, defaultBasketData } from 'constant';
import { FormikValues, useFormik } from 'formik';
import {
  useAddedPaymentMethodMutation,
  useGetPaymentMethodsQuery
} from 'services/paymentMethodsApi';
import {
  useAddCreditCardPaymentMutation,
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation
} from 'services/shoppingCartApi';

import { Box, Checkbox, Flex, Stack, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  clearAllFulfillmentPreferences,
  setError
} from 'store/reducers/cartSlice';

import { formatCurrency, removeSpaces } from 'utils';
import { RawFormData } from 'utils/cayanFormHelper';

import { CCardBase, CCardInterface, ScreenTypeEnum } from 'types';

import { AddCardForm } from 'components/AddCardForm';
import { CreditCardList } from 'components/CreditCardList';
import { ErrorModal } from 'components/PaymentList';
import { Button, Loader, MultiStepHeader, PaymentLoader, Text } from 'ui';
import { NavigationButtons } from '../NavigationButtons';

const initialValues: FormikValues = {
  card_number: '',
  card_cvv2: '',
  exp_month: '',
  exp_year: '',
  owner: '',
  addr_ln1: '',
  city: '',
  state: '',
  zip: ''
};

const {
  payment: { title: paymentTitle, description },
  shop: {
    packages: {
      purchaseStep: { title, subTitle }
    }
  }
} = config;

interface PackagesPaymentStepProps {
  onSuccessPurchase: () => void;
}

const PackagesPaymentStep = ({
  onSuccessPurchase
}: PackagesPaymentStepProps) => {
  const {
    colors: {
      palette: {
        shop: {
          packages: { stepPayment }
        }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const [mode, setMode] = useState<ScreenTypeEnum>(ScreenTypeEnum.cardList);

  const { errorMsg, isLoading: isCartLoading } = useAppSelector(
    (state) => state.cart
  );

  const [isCardProcessing, setIsCardProcessing] = useState<boolean>(false);
  const [shouldSaveCard, setShouldSaveCard] = useState<boolean>(false);

  const { data: shopData = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged
  });
  const { data: clientAccounts = [], isLoading: isCartListLoading } =
    useGetPaymentMethodsQuery('', {
      refetchOnMountOrArgChange: true,
      skip: !!guestInfo
    });
  const [
    addCreditCard,
    {
      error: addErrorMsg = '',
      reset: clearErrors,
      isLoading: isAddingPaymentCard
    }
  ] = useAddedPaymentMethodMutation();
  const [addCreditCardPayment, { isLoading: isAdding }] =
    useAddCreditCardPaymentMutation();
  const [removeGiftCardPayment, { isLoading: isRemoving }] =
    useRemoveGiftCardPaymentMutation();

  const selectedCards = shopData.payments.filter(
    (payment) => !payment?.is_gift_card
  );

  const totalCost = shopData.details.total_cost;
  const invoiceId = guestInfo?.invoice_id || shopData.details.invoice_id;

  const onCartCheckout = async () =>
    dispatch(cartExecutePayment(invoiceId!, onSuccessPurchase));

  const handleSaveCardChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShouldSaveCard(e.target.checked);
  };

  const onScreenTypeChange = useCallback(
    (type: ScreenTypeEnum) => {
      setMode(type);
    },
    [setMode]
  );

  const handleClearError = () => {
    if (errorMsg) {
      dispatch(setError(''));
    }
  };

  const onCardSelect = async (id: number, isExist: boolean) => {
    if (isExist) {
      const currentCard = shopData.payments.find(
        (payment) => payment?.acct_id === id
      );

      await removeGiftCardPayment({
        in_pymt_id: currentCard?.in_pymt_id ?? 0,
        invoice_id: shopData.details.invoice_id ?? 0
      });
    } else {
      await addCreditCardPayment({
        acct_id: id,
        invoice_id: invoiceId!
      });

      if (selectedCards.length > 0) {
        const prevCard = selectedCards.find(
          (payment) => payment?.acct_id !== id
        );

        await removeGiftCardPayment({
          in_pymt_id: prevCard?.in_pymt_id ?? 0,
          invoice_id: shopData.details.invoice_id ?? 0
        });
      }
    }
  };
  const clearPaymentLoderState = () => setIsCardProcessing(false);

  const handleOttPurchase = async (formData: FormikValues) => {
    setIsCardProcessing(true);
    try {
      await dispatch(
        cartExecuteOttPayment(
          formData as RawFormData,
          async (OTTToken: string) => {
            await cartApi.cartExecutePaymentWithOtt({
              invoice_id: Number(invoiceId),
              cc_ott_token: OTTToken
            });
            await onSuccessPurchase();
            // Clear fulfillment preferences after successful OTT payment
            dispatch(clearAllFulfillmentPreferences());
          }
        )
      );
    } catch {
      console.error('Error occurred while ott purchase');
    } finally {
      setIsCardProcessing(false);
      setShouldSaveCard(false);
    }
  };

  const formikData = useFormik({
    initialValues,
    validationSchema: creditCardSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (formData: FormikValues, { resetForm }) => {
      try {
        if (userInfo) {
          if (shouldSaveCard) {
            const cardData: CCardInterface = {
              ...(formData as CCardBase),
              card_number: removeSpaces(formData.card_number),
              allow_billing: 2,
              exp_month: +formData.exp_month,
              exp_year: +formData.exp_year,
              clid: userInfo.clid || 0,
              token: userInfo.token || '0'
            };

            const card = await addCreditCard(cardData).unwrap();

            if (card?.id) {
              onCardSelect(card?.id, false);
              resetForm();
              onScreenTypeChange(ScreenTypeEnum.cardList);
            }
          } else {
            await handleOttPurchase(formData);
          }
        }
      } finally {
        clearPaymentLoderState();
        setShouldSaveCard(false);
      }
    }
  });

  const onAnotherCard = () => {
    formikData.resetForm(initialValues);
    onScreenTypeChange(ScreenTypeEnum.newCardForm);
  };

  useEffect(
    () => {
      if (isCartListLoading || isCartLoading) return;
      if (clientAccounts === null || clientAccounts.length === 0) {
        if (mode !== ScreenTypeEnum.newCardForm) {
          onAnotherCard();
        }
        return;
      }
      if (clientAccounts.length > 0) {
        if (mode !== ScreenTypeEnum.cardList) {
          onScreenTypeChange(ScreenTypeEnum.cardList);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccounts, isCartListLoading, isCartLoading]
  );

  const onPurchase = async () => {
    setIsCardProcessing(true);
    try {
      if (guestInfo) {
        await handleOttPurchase(formikData.values);
      } else {
        await onCartCheckout();
      }
    } finally {
      clearPaymentLoderState();
    }
  };

  const { dirty, isValid } = formikData;
  const isSubmitDisabled = !dirty || !isValid || isCartLoading;

  const renderContent = () => {
    switch (mode) {
      case ScreenTypeEnum.newCardForm:
        return (
          <Flex flexDirection='column' gap='20px' w='full'>
            <AddCardForm
              formikData={formikData}
              errorMsg={addErrorMsg as string}
              clearErrors={clearErrors}
            />
            <Flex my='10px' justifyContent='center'>
              <Checkbox
                isChecked={shouldSaveCard}
                onChange={handleSaveCardChange}
                size='md'
              >
                <Text
                  ml='4px'
                  fontSize='14px'
                  color='gray.600'
                  textTransform='uppercase'
                >
                  Save this credit card for future use
                </Text>
              </Checkbox>
            </Flex>
            <Flex
              direction={{ base: 'column-reverse', md: 'row' }}
              justifyContent='center'
              alignItems='center'
              gap={{ base: '20px', md: '30px' }}
              w='100%'
            >
              <Button
                w='272px'
                h='45px'
                isDisabled={isSubmitDisabled || isAdding || isAddingPaymentCard}
                onClick={() => formikData.handleSubmit()}
              >
                {shouldSaveCard
                  ? 'Save'
                  : `Pay ${formatCurrency(shopData.details.total_cost)}`}
              </Button>

              <Button
                variant='outline'
                w='272px'
                h='45px'
                onClick={() => {
                  onScreenTypeChange(ScreenTypeEnum.cardList);
                  formikData.resetForm();
                }}
              >
                Cancel
              </Button>
            </Flex>
          </Flex>
        );
      default:
        return (
          <Box mx='auto'>
            <CreditCardList
              onAnotherCard={onAnotherCard}
              handleClearError={handleClearError}
              onSelect={onCardSelect}
              selectedCards={selectedCards}
              isLoading={isAdding || isRemoving}
            />

            <NavigationButtons
              nextLabel={`pay ${formatCurrency(totalCost)}`}
              onNextHandler={onPurchase}
              isDisabled={selectedCards.length === 0}
            />
          </Box>
        );
    }
  };

  return (
    <Stack w='100%' mx='auto' gap={0}>
      <MultiStepHeader
        title={title}
        subTitle={subTitle}
        headingColor={stepPayment.heading}
        descriptionColor={stepPayment.text}
        mt={0}
        mb={{ base: '20px', md: '40px' }}
      />

      {renderContent()}

      {isCardProcessing && (
        <PaymentLoader
          color={stepPayment.overlayTextColor}
          bg={stepPayment.overlayBg}
          text={`Processing Payment of ${formatCurrency(totalCost)}`}
        />
      )}
      <ErrorModal
        isOpen={Boolean(errorMsg)}
        onClose={handleClearError}
        onPaymentAgain={onPurchase}
        onAnotherCard={onAnotherCard}
        title={paymentTitle}
        description={description}
      />
      {isCartLoading && <Loader fixed bg='blackOpacity.10' />}
    </Stack>
  );
};

export default PackagesPaymentStep;
